<template>
  <div class="edit-automation-drawer flex flex-col h-full overflow-y-auto">
    <!-- Header -->
    <div v-if="!meta.alwaysOn" class="flex items-center gap-3 py-4 border-b border-surface-200">
      <div
        class="icon-box"
        :style="{ backgroundColor: meta.color + '18', color: meta.color }"
      >
        <i :class="['pi', meta.icon]"></i>
      </div>
      <div class="flex-1 min-w-0">
        <div class="font-medium text-[15px]">{{ meta.name }}</div>
        <div class="text-xs text-surface-500 mt-0.5">{{ getWhenLabel(meta) }}</div>
      </div>
      <InputSwitch v-model="localConfig.enabled" />
    </div>

    <!-- Scrollable body -->
    <div class="flex-1 overflow-y-auto py-4">
      <!-- Smart Nudge — loyalty filter -->
      <template v-if="meta.type === 'smart-nudge'">
        <div class="flex items-center justify-between gap-3 mb-4 p-3 bg-surface-50 border border-surface-200 rounded-[10px]">
          <div class="flex flex-col gap-0.5 flex-1">
            <span class="text-sm font-medium">שליחה ללקוחות שאינם חברי מועדון</span>
            <span class="text-xs text-surface-500">אם מכובה — תישלח הודעה רק לחברי מועדון הלקוחות שלך.</span>
          </div>
          <InputSwitch v-model="localConfig.settings.sendToNonLoyaltyMembers" />
        </div>
        <div
          v-if="localConfig.settings.sendToNonLoyaltyMembers"
          class="flex items-start gap-2 mt-2 p-3 bg-amber-50 border mb-4 border-amber-200 rounded-[10px] text-xs text-amber-800 leading-relaxed"
        >
          <i class="pi pi-exclamation-triangle text-amber-600 text-sm mt-0.5"></i>
          <span>
            שליחה ללקוחות שאינם חברי מועדון עלולה להיחשב כהפרה של חוק הספאם (תיקון 40 לחוק התקשורת). מומלץ לוודא שיש לך הסכמה מפורשת מהלקוחות לפני הפעלת האפשרות.
          </span>
        </div>
      </template>

      <!-- Channel toggle -->
      <div v-if="meta.supportsWhatsApp" class="section-label">ערוץ שליחה</div>
      <div v-if="meta.supportsWhatsApp" class="grid grid-cols-2 gap-2 mb-5">
        <div
          class="channel-option"
          :class="{ selected: localConfig.channel === 'whatsapp', disabled: !canUseWhatsApp }"
          @click="canUseWhatsApp && (localConfig.channel = AutomationChannelEnum.WhatsApp)"
        >
          <div
            v-if="localConfig.channel !== 'whatsapp' && canUseWhatsApp"
            class="badge-recommended"
          >מומלץ</div>
          <div class="flex items-center gap-2.5 mb-1.5">
            <div class="channel-icon" style="background: #dcfce7; color: #15803d;">
              <i class="pi pi-whatsapp text-sm"></i>
            </div>
            <span class="text-sm font-medium">WhatsApp</span>
            <div v-if="localConfig.channel === 'whatsapp'" class="check-circle mr-auto">
              <i class="pi pi-check !text-xs"></i>
            </div>
          </div>
          <div v-if="canUseWhatsApp" class="text-xs text-surface-500">תבנית מאושרת</div>
          <div v-else class="text-xs text-surface-400">{{ waDisabledReason }}</div>
        </div>
        <div
          class="channel-option"
          :class="{ selected: localConfig.channel === 'sms' }"
          @click="localConfig.channel = AutomationChannelEnum.SMS"
        >
          <div class="flex items-center gap-2.5 mb-1.5">
            <div class="channel-icon bg-primary-100 text-primary-600">
              <i class="pi pi-envelope text-sm"></i>
            </div>
            <span class="text-sm font-medium">SMS</span>
            <div v-if="localConfig.channel === 'sms'" class="check-circle mr-auto">
              <i class="pi pi-check !text-xs"></i>
            </div>
          </div>
          <div class="text-xs text-surface-500">טקסט חופשי</div>
        </div>
      </div>

      <!-- Settings fields -->
      <template v-if="meta.settingsSchema.length > 0">
        <div class="section-label">הגדרות</div>
        <template v-for="field in meta.settingsSchema" :key="field.key">
          <div
            v-if="field.type === 'select'"
            class="flex flex-col gap-2 mb-4 p-2.5 bg-surface-50 border border-surface-200 rounded-[10px]"
          >
            <div class="flex items-center gap-3">
              <i class="pi pi-users text-surface-500"></i>
              <span class="text-sm">{{ field.label }}</span>
            </div>
            <div class="flex gap-2">
              <button
                v-for="option in field.options"
                :key="String(option.value)"
                type="button"
                :class="['setting-option', { active: localConfig.settings[field.key] === option.value }]"
                @click="localConfig.settings[field.key] = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
          <div
            v-else
            class="flex items-center gap-3 mb-4 p-2.5 bg-surface-50 border border-surface-200 rounded-[10px]"
          >
            <i class="pi pi-clock text-surface-500"></i>
            <span class="text-sm flex-none">{{ field.label }}</span>
            <InputNumber
              v-if="field.type === 'number'"
              v-model="localConfig.settings[field.key]"
              :min="1"
              :max="field.max"
              inputClass="text-center !w-16"
            />
            <span v-if="field.suffix" class="text-[13px] text-surface-500">{{ field.suffix }}</span>
          </div>
        </template>
      </template>
      <!-- Google Reviews ID (review-request only) -->
      <template v-if="meta.type === 'review-request'">
        <div class="flex items-center justify-between gap-3 mt-4 mb-2 p-3 bg-surface-50 border border-surface-200 rounded-[10px]">
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium">הצגת ביקורות באתר</span>
            <span class="text-xs text-surface-500">האם להציג את הביקורות שהתקבלו מהלקוחות באתר העסק?</span>
          </div>
          <InputSwitch v-model="localConfig.settings.showReviewsOnWebsite" class="flex-none" />
        </div>
        <BusinessSearch
          class="mb-4"
          v-model="localConfig.settings.googleMapsReviewsId"
          @business-selected="onGoogleBusinessSelected"
        />
      </template>

      <!-- Message content -->
      <!-- Smart Nudge — read-only preview for the selected channel -->
      <template v-if="meta.type === 'smart-nudge'">
        <template v-if="localConfig.channel === 'sms'">
          <div class="section-label">תוכן ההודעה (SMS)</div>
          <div class="flex justify-center mb-4">
            <div class="sms-preview-phone">
              <div class="text-[10px] text-surface-500 text-center mb-2">תצוגה מקדימה</div>
              <div class="sms-bubble">{{ smartNudgeSmsPreview }}</div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="flex items-center justify-between mb-2">
            <div class="section-label mb-0">תבנית WhatsApp</div>
            <span class="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
              ✓ אושרה על ידי Meta
            </span>
          </div>
          <div class="wa-preview-container">
            <div class="text-[10px] text-green-700 font-medium mb-2 uppercase tracking-wide">תצוגה מקדימה</div>
            <div class="wa-bubble">{{ smartNudgeWhatsAppPreview }}</div>
          </div>
        </template>

        <div class="flex items-center gap-2 mt-2.5 p-2.5 bg-white border border-dashed border-surface-300 rounded-[10px] text-xs text-surface-600">
          <i class="pi pi-info-circle text-surface-400 text-sm"></i>
          ההודעות נוצרות אוטומטית על בסיס ניתוח מחזור הביקורים של כל לקוח/ה. הטקסט לא ניתן לעריכה.
        </div>
      </template>

      <!-- Regular automations — editable SMS or read-only WhatsApp -->
      <template v-else>
        <!-- SMS Composer -->
        <template v-if="localConfig.channel === 'sms'">
          <div class="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div class="section-label mb-0">תוכן ההודעה</div>
            <div class="flex gap-1 flex-wrap">
              <span
                v-for="tag in meta.availableTags"
                :key="tag"
                :class="['tag-chip', isTagMissing(tag) && 'tag-chip--missing']"
                @click="insertTag(tag)"
              >
                + {{ '{' + tag + '}' }}
              </span>
            </div>
          </div>
          <Textarea
            v-model="localConfig.message"
            rows="4"
            class="w-full text-[13px]"
            :class="{ '!border-red-400': missingMandatoryTags.length > 0 }"
            dir="rtl"
          />
          <div class="flex justify-between mt-1.5 text-xs text-surface-500">
            <span>SMS · 1 הודעה</span>
            <span>{{ localConfig.message?.length || 0 }} / 160 תווים</span>
          </div>

          <div
            v-if="missingMandatoryTags.length > 0"
            class="flex items-start gap-2 mt-2 p-2.5 bg-red-50 border border-red-200 rounded-[10px] text-xs text-red-700 leading-relaxed"
          >
            <i class="pi pi-exclamation-circle text-red-500 text-sm mt-0.5"></i>
            <span>
              חובה לכלול בהודעה את כל התגיות:
              <span v-for="tag in missingMandatoryTags" :key="tag" class="font-medium mx-0.5">{{ '{' + tag + '}' }}</span>
            </span>
          </div>

          <!-- SMS Preview -->
          <div class="flex justify-center mt-4">
            <div class="sms-preview-phone">
              <div class="text-[10px] text-surface-500 text-center mb-2">תצוגה מקדימה</div>
              <div class="sms-bubble">
                {{ previewMessage }}
              </div>
            </div>
          </div>
        </template>

        <!-- WhatsApp Template View -->
        <template v-else>
          <div class="flex items-center justify-between mb-2">
            <div class="section-label mb-0">תבנית WhatsApp</div>
            <span class="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
              ✓ אושרה על ידי Meta
            </span>
          </div>

          <!-- Business-written paragraph inside the template -->
          <template v-if="meta.whatsAppFreeText">
            <div class="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div class="section-label mb-0">הטקסט שלכם בתוך התבנית</div>
              <div class="flex gap-1 flex-wrap">
                <span
                  v-for="tag in meta.availableTags"
                  :key="tag"
                  class="tag-chip"
                  @click="insertFreeTextTag(tag)"
                >
                  + {{ '{' + tag + '}' }}
                </span>
              </div>
            </div>
            <Textarea
              v-model="localConfig.settings.whatsappFreeText"
              rows="3"
              class="w-full text-[13px] mb-1.5"
              :class="{ '!border-red-400': !freeTextValid }"
              :maxlength="WHATSAPP_FREE_TEXT_MAX_LENGTH"
              dir="rtl"
            />
            <div class="flex justify-between mb-3 text-xs text-surface-500">
              <span>שורה אחת — ירידות שורה יוחלפו ברווח</span>
              <span>{{ localConfig.settings.whatsappFreeText?.length || 0 }} / {{ WHATSAPP_FREE_TEXT_MAX_LENGTH }}</span>
            </div>
          </template>

          <!-- WA preview bubble -->
          <div class="wa-preview-container">
            <div class="text-[10px] text-green-700 font-medium mb-2 uppercase tracking-wide">תצוגה מקדימה</div>
            <div class="wa-bubble">
              {{ whatsAppPreviewMessage }}
            </div>
          </div>

          <div class="flex items-center gap-2 mt-2.5 p-2.5 bg-white border border-dashed border-surface-300 rounded-[10px] text-xs text-surface-600">
            <i class="pi pi-star text-surface-400 text-sm"></i>
            <template v-if="meta.whatsAppFreeText">שאר הטקסט בתבנית מאושר מראש ולא ניתן לעריכה.</template>
            <template v-else>הטקסט של תבניות WhatsApp מאושר מראש ולא ניתן לעריכה.</template>
          </div>
        </template>
      </template>

      <!-- Birthday requires loyalty program -->
      <div v-if="missingLoyalty" class="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div class="flex items-start gap-2.5">
          <i class="pi pi-gift text-amber-500 text-lg mt-0.5"></i>
          <div class="flex-1">
            <p class="text-sm font-medium text-amber-800 mb-1">נדרש מועדון לקוחות</p>
            <p class="text-xs text-amber-700 leading-relaxed mb-3">
              כדי לשלוח ברכות יום הולדת, צריך קודם להפעיל מועדון לקוחות ולאסוף תאריכי יום הולדת.
            </p>
            <Button
              label="הפעלת מועדון לקוחות"
              icon="pi pi-arrow-left"
              iconPos="right"
              size="small"
              class="!rounded-full"
              @click="goToLoyalty"
            />
          </div>
        </div>
      </div>

      <!-- Invoice receipt requires a supported invoice provider (PayMe / SmartBee) -->
      <div v-if="missingInvoiceProvider" class="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div class="flex items-start gap-2.5">
          <i class="pi pi-receipt text-amber-500 text-lg mt-0.5"></i>
          <div class="flex-1">
            <p class="text-sm font-medium text-amber-800 mb-1">נדרש חיבור ספק חשבוניות</p>
            <p class="text-xs text-amber-700 leading-relaxed mb-3">
              שליחת חשבוניות אוטומטית עובדת עם ספק חשבוניות נתמך (PayMe או SmartBee). יש להגדיר את החיבור במסך אמצעי התשלום לפני הפעלת האוטומציה.
            </p>
            <Button
              label="להגדרת אמצעי תשלום"
              icon="pi pi-arrow-left"
              iconPos="right"
              size="small"
              class="!rounded-full"
              @click="goToPaymentSettings"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <drawer-footer
      :approveLoading="saving"
      :approveDisable="!canSave"
      @approve-click="save"
      @cancel-click="cancel"
    />
  </div>
</template>

<script lang="ts" setup>
import type { IAutomationConfig, IAutomationTypeMeta } from '@torim-app/types';
import { AutomationChannelEnum, WHATSAPP_FREE_TEXT_MAX_LENGTH } from '@torim-app/types';
import { reactive, ref, computed } from 'vue';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import { useDrawer } from '@torim-app/shared-components';
import BusinessSearch from '../../components/BusinessSearch.vue';
import { useRouter } from 'vue-router';
import { AdminRouteNames } from '@app/router/route-names';

const props = defineProps<{
  config: IAutomationConfig;
  meta: IAutomationTypeMeta;
  onSave: (config: IAutomationConfig) => Promise<void>;
  hasWhatsApp?: boolean;
  hasLoyalty?: boolean;
  hasSupportedInvoiceProvider?: boolean;
}>();

const { closeDrawer } = useDrawer();
const router = useRouter();
const saving = ref(false);

const localConfig = reactive<IAutomationConfig>({
  ...props.config,
  settings: { ...props.config.settings },
});

const waUnavailable = !props.meta.supportsWhatsApp
  || (props.meta.requiresBusinessWhatsApp && !props.hasWhatsApp);
if (waUnavailable && localConfig.channel === AutomationChannelEnum.WhatsApp) {
  localConfig.channel = AutomationChannelEnum.SMS;
}

if (props.meta.whatsAppFreeText && localConfig.settings.whatsappFreeText === undefined) {
  localConfig.settings.whatsappFreeText = props.meta.whatsAppFreeText.defaultText;
}

// Choice fields need a selected option even for configs saved before the field existed.
for (const field of props.meta.settingsSchema) {
  if (field.type === 'select' && localConfig.settings[field.key] === undefined && field.default !== undefined) {
    localConfig.settings[field.key] = field.default;
  }
}

if (props.meta.type === 'smart-nudge' && localConfig.settings.sendToNonLoyaltyMembers === undefined) {
  localConfig.settings.sendToNonLoyaltyMembers = false;
}

const canUseWhatsApp = computed(() => {
  if (!props.meta.supportsWhatsApp) return false;
  if (props.meta.requiresBusinessWhatsApp) return !!props.hasWhatsApp;
  return true;
});

const waDisabledReason = computed(() => {
  if (!props.meta.supportsWhatsApp) return 'לא זמין עבור אוטומציה זו';
  if (props.meta.requiresBusinessWhatsApp && !props.hasWhatsApp) return 'דורש חיבור חשבון WhatsApp Business';
  return '';
});

const requiresLoyalty = computed(() => props.meta.type === 'birthday-greeting');
const missingLoyalty = computed(() => requiresLoyalty.value && !props.hasLoyalty);
const missingInvoiceProvider = computed(() => !!props.meta.requiresInvoiceProvider && !props.hasSupportedInvoiceProvider);

const missingMandatoryTags = computed<string[]>(() => {
  if (localConfig.channel !== AutomationChannelEnum.SMS && localConfig.channel !== AutomationChannelEnum.Both) return [];
  const required = props.meta.mandatoryTags
    ?? (props.meta.allTagsMandatory ? props.meta.availableTags : []);
  if (!required.length) return [];
  const message = localConfig.message || '';
  return required.filter(tag => !message.includes(`{${tag}}`));
});

// The template's free-text slot can't be sent empty
const freeTextValid = computed(() =>
  !props.meta.whatsAppFreeText
  || localConfig.channel !== AutomationChannelEnum.WhatsApp
  || !!localConfig.settings.whatsappFreeText?.trim()
);

const canSave = computed(() => !missingLoyalty.value && !missingInvoiceProvider.value && missingMandatoryTags.value.length === 0 && freeTextValid.value);

const getWhenLabel = (meta: IAutomationTypeMeta): string => {
  const labels: Record<string, string> = {
    'booking-confirmation': 'כשלקוח קובע פגישה',
    'booking-canceled': 'כשלקוח מבטל פגישה',
    'appointment-rescheduled': 'כשהתור מעודכן',
    'abandoned-scheduling': 'אחרי נטישת הזמנה',
    'inactive-customer': 'לקוח לא פעיל',
    'post-visit-follow-up': 'אחרי ביקור',
    'birthday-greeting': 'בוקר יום ההולדת',
    'smart-nudge': 'מבוסס מחזור ביקורים',
    'no-show-follow-up': 'אחרי אי-הגעה',
    'review-request': 'אחרי ביקור',
    'new-customer-welcome': 'אחרי ההזמנה הראשונה',
    'same-day-reminder': 'בבוקר יום התור',
  };
  return labels[meta.type] || '';
};

const fillPreviewTags = (text: string): string => {
  return text
    .replace(/{שם-הלקוח}/g, 'נועה')
    .replace(/{שם}/g, 'נועה')
    .replace(/{תאריך}/g, '23 באפריל 2026')
    .replace(/{תאריך-קודם}/g, '20 באפריל 2026')
    .replace(/{שעה}/g, '09:00')
    .replace(/{שם-העסק}/g, 'הסטודיו של דנה')
    .replace(/{סניף}/g, 'סניף תל אביב')
    .replace(/{כתובת-סניף}/g, 'דיזנגוף 100, תל אביב')
    .replace(/{שירותים}/g, 'תספורת')
    .replace(/{קישור-אישור}/g, 'https://time-it.co.il/...')
    .replace(/{קישור-הזמנה}/g, 'https://time-it.co.il/...')
    .replace(/{קישור-משוב}/g, 'https://time-it.co.il/...')
    .replace(/{קישור}/g, 'https://time-it.co.il/...');
};

const previewMessage = computed(() => fillPreviewTags(localConfig.message || ''));

const whatsAppPreviewMessage = computed(() => {
  const msg = props.meta.defaultWhatsAppMessage || props.meta.defaultMessage || '';
  if (!props.meta.whatsAppFreeText)
    return msg
      .replace(/{שם-הלקוח}/g, 'נועה')
      .replace(/{שם}/g, 'נועה');
  const freeText = (localConfig.settings.whatsappFreeText || '').replace(/\s*\n+\s*/g, ' ').trim();
  return fillPreviewTags(msg.replace('{טקסט-חופשי}', freeText));
});

const smartNudgeSmsPreview = computed(() => {
  return (props.meta.defaultMessage || '')
    .replace(/{שם-הלקוח}/g, 'נועה')
    .replace(/{מחזור}/g, 'חודש')
    .replace(/{שירותים}/g, 'תספורת')
    .replace(/{שם-העסק}/g, 'הסטודיו של דנה')
    .replace(/{סניף}/g, 'סניף תל אביב')
    .replace(/{כתובת-סניף}/g, 'דיזנגוף 100, תל אביב')
    .replace(/{תורים-פנויים}/g, 'יום ראשון 10:00, יום שלישי 14:00');
});

const smartNudgeWhatsAppPreview = computed(() => {
  return (props.meta.defaultWhatsAppMessage || props.meta.defaultMessage || '')
    .replace(/{שם-הלקוח}/g, 'נועה')
    .replace(/{מחזור}/g, 'חודש')
    .replace(/{שירותים}/g, 'תספורת')
    .replace(/{שם-העסק}/g, 'הסטודיו של דנה')
    .replace(/{סניף}/g, 'סניף תל אביב')
    .replace(/{כתובת-סניף}/g, 'דיזנגוף 100, תל אביב')
    .replace(/{תורים-פנויים}/g, 'יום ראשון 10:00, יום שלישי 14:00');
});

const insertTag = (tag: string) => {
  localConfig.message = (localConfig.message || '') + `{${tag}}`;
};

const insertFreeTextTag = (tag: string) => {
  localConfig.settings.whatsappFreeText = (localConfig.settings.whatsappFreeText || '') + `{${tag}}`;
};

const isTagMissing = (tag: string) => missingMandatoryTags.value.includes(tag);

const onGoogleBusinessSelected = (data: { placeId: string; directReviewLink: string; reviewsPageLink: string }) => {
  localConfig.settings.googleMapsReviewsId = data.placeId;
};

const goToLoyalty = () => {
  closeDrawer();
  router.push({ name: AdminRouteNames.LoyaltyProgram });
};

const goToPaymentSettings = () => {
  closeDrawer();
  router.push({ name: AdminRouteNames.Integrations });
};

const cancel = () => {
  closeDrawer();
};

const save = async () => {
  saving.value = true;
  try {
    await props.onSave({ ...localConfig, settings: { ...localConfig.settings } });
    closeDrawer();
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped lang="scss">
.setting-option {
  @apply flex-1 text-[13px] py-2 px-3 rounded-lg border border-surface-200 bg-white text-surface-600 transition-colors;

  &.active {
    @apply border-primary bg-primary-50 text-primary font-medium;
  }
}

.edit-automation-drawer {
  .icon-box {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    i { font-size: 1.25rem; }
  }

  .section-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--p-surface-700);
    margin-bottom: 8px;
    letter-spacing: 0.3px;
  }

  .channel-option {
    padding: 12px 14px;
    border-radius: 12px;
    border: 2px solid var(--p-surface-200);
    background: #fff;
    cursor: pointer;
    position: relative;
    transition: border-color 0.15s, background 0.15s;

    &.selected {
      border-color: rgb(var(--primary));
      background: linear-gradient(135deg, #f5f4fe 0%, #f0eefa 100%);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .badge-recommended {
    position: absolute;
    top: -8px;
    right: 10px;
    background: rgb(var(--primary));
    color: #fff;
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 500;
  }

  .channel-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .check-circle {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: rgb(var(--primary));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tag-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 7px;
    background: var(--p-primary-50);
    color: var(--p-primary-700);
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: var(--p-primary-100); }

    &--missing {
      background: #fee2e2;
      color: #b91c1c;
      &:hover { background: #fecaca; }
    }
  }

  .sms-preview-phone {
    width: 260px;
    border-radius: 22px;
    border: 1px solid var(--p-surface-200);
    background: var(--p-surface-50);
    padding: 14px;
  }

  .sms-bubble {
    background: #fff;
    padding: 10px 14px;
    border-radius: 14px;
    border-top-right-radius: 4px;
    font-size: 12.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    box-shadow: 0 1px 1px rgba(0,0,0,0.04);
  }

  .wa-preview-container {
    background: #e7f3ed;
    border-radius: 14px;
    padding: 16px;
  }

  .wa-bubble {
    background: #dcf8c6;
    padding: 10px 14px;
    border-radius: 14px;
    border-top-right-radius: 4px;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    box-shadow: 0 1px 1px rgba(0,0,0,0.04);
  }
}
</style>
